import React, { FC, ReactNode, Ref, useEffect, useId, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import type { Crm } from "../../components/CrmUi/useCrm";
import { withCrm } from "../../components/CrmUi/authCrm";
import { LogoSquircle, LogoWordmark } from "../../components/Display/Logo";
import { EMBED_COLOR } from "../../services/supportChat";
import { HubSpotMark, OutreachMark } from "../../components/Settings/systems";
import { HUBSPOT_FONT_URL } from "../../components/HubSpotUi/theme";
import loginImage from "../../assets/images/login-image.svg";
import "./auth.css";

/**
 * The parts boring's sign-in, sign-up, reset-password and connect pages are
 * built from, each drawn in the look of the CRM the person came for: HubSpot's
 * centred white column and teal pills, or Outreach's split brand page and
 * slate pills (auth.css has the measurements). One component per part, so a
 * page's logic is written once and only the skin changes.
 */

const TERMS_URL = "https://boringplugins.com/terms-of-service";
const PRIVACY_URL = "https://boringplugins.com/privacy-policy";

/** boring's logo, "for", and the CRM's name: ours, never the CRM's logo as ours. */
export const AuthLockup: FC<{ crm: Crm }> = ({ crm }) => {
  const crmName = crm === "hubspot" ? "HubSpot" : "Outreach";
  if (crm === "outreach") {
    return (
      <div className="ba-ou-wordmark" role="img" aria-label={`boring for ${crmName}`}>
        <span className="ba-ou-boring" aria-hidden="true">
          <svg viewBox="100 170 320 190">
            <circle cx="145.5" cy="215.1" r="35.9" fill="#ffffff" />
            <circle cx="280" cy="215.1" r="35.9" fill="#ffffff" />
            <path
              d="M151.875 335.457C292.711 366.069 378.738 318.397 404.147 290.734"
              stroke="#ffffff"
              strokeWidth="20"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
          boring
        </span>
        <span className="ba-ou-for" aria-hidden="true">
          for <OutreachMark /> {crmName}
        </span>
      </div>
    );
  }
  return (
    <div className="ba-lockup" role="img" aria-label={`boring for ${crmName}`}>
      <span className="ba-lockup-squircle" aria-hidden="true">
        <LogoSquircle />
      </span>
      <span className="ba-lockup-word" aria-hidden="true">
        <LogoWordmark />
      </span>
      <span className="ba-lockup-for" aria-hidden="true">
        for
      </span>
      <span className="ba-lockup-crm" aria-hidden="true">
        <HubSpotMark />
        {crmName}
      </span>
    </div>
  );
};

interface ShellProps {
  crm: Crm;
  /** Beside the form (under it on narrow screens): the onboarding calendar. */
  aside?: ReactNode;
  /** Outreach only: the sign-in illustration over the form. */
  illustration?: boolean;
  children: ReactNode;
}

/** The whole page: the CRM's layout, logo slot and footer around `children`. */
export const AuthShell: FC<ShellProps> = ({ crm, aside, illustration, children }) => {
  // ThemeContext loads Lexend Deca for HubSpot; this covers the connect page,
  // whose CRM can be the sign-up's before the organization says so.
  useEffect(() => {
    if (crm !== "hubspot" || document.getElementById("hubspot-font")) return;
    const link = document.createElement("link");
    link.id = "hubspot-font";
    link.rel = "stylesheet";
    link.href = HUBSPOT_FONT_URL;
    document.head.appendChild(link);
  }, [crm]);

  if (crm === "hubspot") {
    return (
      <div id="boring-auth" className="ba ba--hs">
        <div className="ba-hs-page">
          <main className={`ba-hs-main${aside ? " ba-hs-main--aside" : ""}`}>
            <div className="ba-hs-col">
              <AuthLockup crm={crm} />
              {children}
            </div>
            {aside && <aside className="ba-hs-aside">{aside}</aside>}
          </main>
          <footer className="ba-hs-footer">
            <span>©{new Date().getFullYear()} boring. All rights reserved.</span>
            <a className="ba-link" href={TERMS_URL} target="_blank" rel="noreferrer">
              Terms of Service
            </a>
            <a className="ba-link" href={PRIVACY_URL} target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
          </footer>
        </div>
      </div>
    );
  }
  return (
    <div id="boring-auth" className="ba ba--ou">
      <div className={`ba-ou-grid${aside ? " ba-ou-grid--aside" : ""}`}>
        <div className="ba-ou-brand">
          <AuthLockup crm={crm} />
        </div>
        <main className="ba-ou-form">
          <div className="ba-ou-content">
            {illustration && <img className="ba-ou-illustration" src={loginImage} alt="" />}
            {children}
          </div>
          <footer className="ba-ou-footer">
            <a className="ba-link" href={TERMS_URL} target="_blank" rel="noreferrer">
              Terms
            </a>
            <a className="ba-link" href={PRIVACY_URL} target="_blank" rel="noreferrer">
              Privacy
            </a>
          </footer>
        </main>
        {aside && <aside className="ba-ou-aside">{aside}</aside>}
      </div>
    </div>
  );
};

export const AuthTitle: FC<{ children: ReactNode }> = ({ children }) => (
  <h1 className="ba-title">{children}</h1>
);

/**
 * A title, an optional line under it, and the form (or just its buttons):
 * centred over a left-aligned form for HubSpot, all in Outreach's 326px
 * column for Outreach.
 */
export const AuthPanel: FC<{
  crm: Crm;
  title: ReactNode;
  intro?: ReactNode;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
}> = ({ crm, title, intro, onSubmit, children }) => {
  const head = (
    <>
      <AuthTitle>{title}</AuthTitle>
      {intro && <p className="ba-note">{intro}</p>}
    </>
  );
  // A plain function, not a component: a component made here would be a new
  // type every render and remount the fields (losing focus) on each keystroke.
  const wrap = (content: ReactNode) =>
    onSubmit ? (
      <form className="ba-form" onSubmit={onSubmit} noValidate>
        {content}
      </form>
    ) : (
      <div className="ba-form">{content}</div>
    );
  if (crm === "hubspot") {
    return (
      <>
        {head}
        {wrap(children)}
      </>
    );
  }
  return wrap(
    <>
      {head}
      {children}
    </>
  );
};

interface FieldProps {
  crm: Crm;
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  required?: boolean;
  minLength?: number;
  placeholder?: string;
  /** Help under the field; with `invalid`, the reason it isn't accepted. */
  hint?: string;
  invalid?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  onClick?: () => void;
}

/** A labelled text field: HubSpot's label over a 40px box, or Outreach's floating-label field. */
export const AuthField: FC<FieldProps> = ({
  crm,
  label,
  name,
  value,
  onChange,
  type = "text",
  autoComplete,
  autoFocus,
  readOnly,
  required,
  minLength,
  placeholder,
  hint,
  invalid,
  inputRef,
  onClick,
}) => {
  const id = useId();
  const hintId = `${id}-hint`;

  if (crm === "outreach") {
    return (
      <TextField
        className="ba-field"
        id={id}
        label={label}
        type={type}
        name={name}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required={required}
        placeholder={placeholder}
        value={value}
        error={invalid}
        helperText={hint}
        inputRef={inputRef}
        InputProps={{ readOnly }}
        inputProps={{ minLength }}
        onClick={onClick}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <div className="ba-field">
      <label className="ba-label" htmlFor={id}>
        {label}
      </label>
      <input
        className="ba-input"
        id={id}
        type={type}
        name={name}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        readOnly={readOnly}
        required={required}
        minLength={minLength}
        placeholder={placeholder}
        value={value}
        ref={inputRef}
        aria-invalid={invalid || undefined}
        aria-describedby={hint ? hintId : undefined}
        onClick={onClick}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && (
        <div id={hintId} className={`ba-hint${invalid ? " ba-hint--error" : ""}`}>
          {hint}
        </div>
      )}
    </div>
  );
};

interface ButtonProps {
  type?: "button" | "submit";
  disabled?: boolean;
  /** Shows a spinner in place of the label, announced as `busyLabel`. */
  loading?: boolean;
  busyLabel?: string;
  secondary?: boolean;
  onClick?: () => void;
  children: ReactNode;
}

/** The CRM's full-width pill: primary (teal / slate) or `secondary` (outlined). */
export const AuthButton: FC<ButtonProps> = ({
  type = "button",
  disabled,
  loading,
  busyLabel = "Working",
  secondary,
  onClick,
  children,
}) => (
  <button
    type={type}
    className={`ba-btn${secondary ? " ba-btn--secondary" : ""}`}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    onClick={onClick}
  >
    {loading ? <CircularProgress size={16} color="inherit" aria-label={busyLabel} /> : children}
  </button>
);

/** A quiet text action, like HubSpot's "Sign in a different way". */
export const AuthTextButton: FC<{ onClick: () => void; disabled?: boolean; children: ReactNode }> = ({
  onClick,
  disabled,
  children,
}) => (
  <button type="button" className="ba-textbtn" disabled={disabled} onClick={onClick}>
    {children}
  </button>
);

/** A link to another sign-in page that keeps the CRM (?crm=). */
export const AuthLink: FC<{
  crm: Crm;
  to: string;
  ink?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}> = ({ crm, to, ink, onClick, children }) => (
  <RouterLink className={ink ? "ba-inklink" : "ba-link"} to={withCrm(to, crm)} onClick={onClick}>
    {children}
  </RouterLink>
);

export const AuthError: FC<{ children: ReactNode }> = ({ children }) =>
  children ? (
    <div className="ba-error" role="alert">
      {children}
    </div>
  ) : null;

/** "By creating an account, you agree to ..." under the sign-up button. */
export const AuthLegal: FC<{ action: string }> = ({ action }) => (
  <p className="ba-legal">
    By {action}, you agree to boring&apos;s{" "}
    <a className="ba-inklink" href={TERMS_URL} target="_blank" rel="noreferrer">
      Terms of Service
    </a>{" "}
    and acknowledge its{" "}
    <a className="ba-inklink" href={PRIVACY_URL} target="_blank" rel="noreferrer">
      Privacy Policy
    </a>
    .
  </p>
);

export const PencilIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M10.9 2.6a1.5 1.5 0 0 1 2.1 0l.4.4a1.5 1.5 0 0 1 0 2.1L6 12.5 2.5 13.5l1-3.5 7.4-7.4Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path d="M9.5 4l2.5 2.5" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

// The boring workspace's "boring onboarding" booking link in Everything.
const CALENDAR_SLUG = "boring-onboarding";
const EVERYTHING_ORIGIN = "https://everything.thetool.company";
const CALENDAR_PAGE = `${EVERYTHING_ORIGIN}/book/${CALENDAR_SLUG}`;
const EVENT_NAME = "boring onboarding";

/**
 * "Book a personalized onboarding": Everything's booking page for the boring
 * onboarding call, right in the page (no facade), in the CRM's colour
 * (?color=, the same colour as the chat). It loads with the page: it is
 * the reason for the panel. The booking page reports its content height
 * (an `everything:embed-height` message) and the frame grows to fit, so it
 * never scrolls inside the card; 600px is only the starting height.
 */
export const OnboardingPanel: FC<{ crm: Crm; title?: string; sub?: string }> = ({ crm, title, sub }) => {
  const titleId = useId();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [frameHeight, setFrameHeight] = useState(600);

  const askHeight = () => frameRef.current?.contentWindow?.postMessage({ type: "everything:embed-height-request" }, EVERYTHING_ORIGIN);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== EVERYTHING_ORIGIN || event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { type?: string; height?: unknown } | null;
      if (data?.type !== "everything:embed-height" || typeof data.height !== "number") return;
      setFrameHeight(Math.min(Math.max(Math.ceil(data.height), 320), 4000));
    };
    window.addEventListener("message", onMessage);
    // The page may have reported before this listener existed: ask again.
    askHeight();
    return () => window.removeEventListener("message", onMessage);
  }, []);
  const crmName = crm === "hubspot" ? "HubSpot" : "Outreach";
  // appearance=light: the auth pages are light, so the booking page is too.
  // compact: the week strip and no title block, made for side panels; card=0: it sits flush in this panel's card.
  const src = `${CALENDAR_PAGE}?embed=1&compact=1&card=0&appearance=light&color=${encodeURIComponent(EMBED_COLOR[crm])}`;

  return (
    <section className="ba-onb" aria-labelledby={titleId}>
      <h2 className="ba-onb-title" id={titleId}>
        {title ?? "Book a personalized onboarding"}
      </h2>
      <p className="ba-onb-sub">{sub ?? `Pick a time and we'll set boring up in your ${crmName} with you, on a call.`}</p>
      <div className="ba-cal ba-cal--inline">
        <iframe ref={frameRef} className="ba-cal-iframe" style={{ height: frameHeight }} onLoad={askHeight} src={src} title={`${EVENT_NAME}: pick a time`} />
      </div>
    </section>
  );
};
